import { get } from 'lodash/fp'

import {
  TActiveColorKey,
  TActiveColorKeyPath,
  TElementState,
  TExtendedColorKey,
  TTheme,
} from '../typings'

//
// * --- utility functions (used when working with the theme) ---
//

type TColorKeyOrSpecial = TExtendedColorKey | 'currentColor'

/**
 * returns a color value from provided theme object based on provided color key,
 */
export const getColorValue = (theme: TTheme) => (key: TColorKeyOrSpecial): string => {
  if (key === 'inherit') return 'inherit'
  if (key === 'transparent') return 'transparent'
  if (key === 'currentColor') return 'currentColor'

  return get(key)(theme) ?? theme.specialColors.disabled
}

/**
 * returns true if the provided key points to a color
 * that should be considered disabled
 */
export const isDisabledColor = (color: TColorKeyOrSpecial) => {
  return color.includes(`disabled`)
}

type TGetActiveColorPathOptions = {
  /** state of the element, has lower priority than `isDisabled` and `isHovered`, default is `normal` */
  state?: TElementState
  /** key of active colors from the theme */
  bgColor: TActiveColorKey
  /** same as element state `disabled`, has higher priority */
  isDisabled?: boolean
  /** same as element state `hover`, has higher priority */
  isHovered?: boolean
}

/**
 * returns a full path of active element color based on selected active color
 * and elements state,
 * the state can be also provided using flag options that has higher priority that the `state` option,
 * if no state is provided, default value is `normal`
 */
export const getActiveColorFullPath = ({
  bgColor,
  isHovered,
  isDisabled,
  state = `normal`,
}: TGetActiveColorPathOptions): TActiveColorKeyPath => {
  if (isDisabled) return `activeColors.${bgColor}.disabled`
  if (isHovered) return `activeColors.${bgColor}.hover`

  return `activeColors.${bgColor}.${state}`
}
